// Workaround de facebook/react#11538: el traductor del navegador envuelve nodos de texto en
// <font>, y luego removeChild/insertBefore de React lanzan "The node to be removed is not a
// child of this node". Solo se tolera en páginas traducidas; en el resto se llama al método
// original para que los errores reales sigan lanzando.

let installed = false;
let warned = false;

function warnOnce(method: string): void {
  if (warned) return;
  warned = true;
  // eslint-disable-next-line no-console
  console.warn(
    `[browserTranslateGuard] ${method} on a moved node (browser translation); recovering.`
  );
}

// Chrome Translate pone `translated-ltr|rtl` en <html> y reescribe `lang`.
function isPageTranslated(): boolean {
  const root = document.documentElement;
  if (/\btranslated-(ltr|rtl)\b/.test(root.className)) return true;
  return !!root.lang && !/^en(-|$)/i.test(root.lang);
}

export function installBrowserTranslateGuard(): void {
  if (installed || typeof Node !== 'function' || !Node.prototype) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode === this || !isPageTranslated()) {
      return originalRemoveChild.call(this, child) as T;
    }
    warnOnce('removeChild');
    // Si el nodo fue movido (p. ej. dentro de un <font>), se quita de su padre real.
    const actualParent = child.parentNode;
    if (actualParent) originalRemoveChild.call(actualParent, child);
    return child;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (!referenceNode || referenceNode.parentNode === this || !isPageTranslated()) {
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    }
    warnOnce('insertBefore');
    // Sube hasta el ancestro que sí es hijo directo; si no hay, se añade al final.
    let anchor: Node | null = referenceNode;
    while (anchor && anchor.parentNode !== this) anchor = anchor.parentNode;
    return originalInsertBefore.call(this, newNode, anchor) as T;
  };
}
